import React, { useState, useEffect, useRef } from 'react';
import api from '@/services/api';
import {
  MessageSquare,
  Send,
  User,
  UserCheck,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Plus,
  Edit2,
  Trash2,
  Sparkles,
  Bot
} from 'lucide-react';

interface ChatSession {
  id: string;
  sessionId: string;
  customerName: string;
  customerPhone?: string;
  status: 'ai' | 'needs_human' | 'human_active' | 'closed';
  lastMessage?: string;
  createdAt: string;
  updatedAt: string;
  unreadByAdmin?: boolean;
}

interface ChatMessage {
  id: string;
  sender: 'customer' | 'ai' | 'admin';
  adminName?: string;
  text: string;
  createdAt: any;
}

interface QuickReply {
  id: string;
  title: string;
  text: string;
}

export const LiveChatManager: React.FC = () => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [replyText, setReplyText] = useState<string>('');
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'needs_human' | 'ai'>('all');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [showQuickReplyModal, setShowQuickReplyModal] = useState<boolean>(false);

  // Quick reply modal editing state
  const [editingReplies, setEditingReplies] = useState<QuickReply[]>([]);
  const [newTitle, setNewTitle] = useState<string>('');
  const [newText, setNewText] = useState<string>('');

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevSessionIdRef = useRef<string | null>(null);
  const justSentReplyRef = useRef<boolean>(false);

  const playNotificationChime = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(587.33, now);
      gain1.gain.setValueAtTime(0.15, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.3);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(880, now + 0.15);
      gain2.gain.setValueAtTime(0.2, now + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.15);
      osc2.stop(now + 0.5);
    } catch (e) {
      console.warn('Audio chime failed:', e);
    }
  };

  const triggerBrowserNotification = (title: string, body: string) => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'granted') {
        new Notification(title, { body, icon: '/favicon.ico' });
      } else if (Notification.permission !== 'denied') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            new Notification(title, { body, icon: '/favicon.ico' });
          }
        });
      }
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await api.get('/admin/chat/sessions');
      if (res.data && res.data.sessions) {
        const newSessions = res.data.sessions as ChatSession[];
        setSessions(prev => {
          if (prev.length > 0) {
            const hasNewSession = newSessions.some(ns => !prev.some(ps => ps.sessionId === ns.sessionId));
            const hasNewUnread = newSessions.some(ns => ns.unreadByAdmin && !prev.some(ps => ps.sessionId === ns.sessionId && ps.unreadByAdmin));
            const hasHandover = newSessions.some(ns => ns.status === 'needs_human' && !prev.some(ps => ps.sessionId === ns.sessionId && ps.status === 'needs_human'));

            if (hasNewSession || hasNewUnread || hasHandover) {
              playNotificationChime();
              triggerBrowserNotification(
                hasHandover ? "🚨 Canlı Temsilci Talebi!" : "🌸 Yeni Canlı Destek Mesajı!",
                "Canlı destek panelinizde ilgilenmeniz gereken yeni bir sohbet var."
              );
            }
          }
          return newSessions;
        });
      }
    } catch (err) {
      console.error('Fetch chat sessions error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuickReplies = async () => {
    try {
      const res = await api.get('/admin/chat/quick-replies');
      if (res.data && res.data.replies) {
        setQuickReplies(res.data.replies);
        setEditingReplies(res.data.replies);
      }
    } catch (err) {
      console.error('Fetch quick replies error:', err);
    }
  };

  const fetchMessages = async (sessionId: string) => {
    try {
      const res = await api.get(`/web/chat/public/messages/${sessionId}`);
      if (res.data && res.data.messages) {
        setMessages(res.data.messages);
      }
    } catch (err) {
      console.error('Fetch messages error:', err);
    }
  };

  useEffect(() => {
    const pollSessions = () => {
      if (document.visibilityState !== 'hidden') {
        fetchSessions();
      }
    };
    pollSessions();
    fetchQuickReplies();
    const interval = setInterval(pollSessions, 12000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedSessionId) {
      const pollMsgs = () => {
        if (document.visibilityState !== 'hidden') {
          fetchMessages(selectedSessionId);
        }
      };
      pollMsgs();
      const interval = setInterval(pollMsgs, 8000);
      return () => clearInterval(interval);
    }
  }, [selectedSessionId]);

  useEffect(() => {
    if (!selectedSessionId || messages.length === 0) return;

    const isNewSession = prevSessionIdRef.current !== selectedSessionId;
    prevSessionIdRef.current = selectedSessionId;

    const container = messagesContainerRef.current;
    let isNearBottom = true;
    if (container) {
      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      isNearBottom = distanceFromBottom <= 120;
    }

    if (isNewSession || justSentReplyRef.current || isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: isNewSession ? 'auto' : 'smooth' });
    }

    justSentReplyRef.current = false;
  }, [messages, selectedSessionId]);

  const handleSendReply = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedSessionId || !replyText.trim() || isSending) return;

    setIsSending(true);
    justSentReplyRef.current = true;
    const textToSend = replyText.trim();
    setReplyText('');

    try {
      await api.post('/admin/chat/reply', {
        sessionId: selectedSessionId,
        message: textToSend,
        adminName: 'Admin',
      });
      await fetchMessages(selectedSessionId);
      await fetchSessions();
    } catch (err) {
      console.error('Send reply error:', err);
    } finally {
      setIsSending(false);
    }
  };

  const handleUpdateStatus = async (status: 'ai' | 'needs_human' | 'human_active' | 'closed') => {
    if (!selectedSessionId) return;
    try {
      await api.post('/admin/chat/status', { sessionId: selectedSessionId, status });
      setSessions(prev => prev.map(s => s.sessionId === selectedSessionId ? { ...s, status } : s));
    } catch (err) {
      console.error('Update chat status error:', err);
    }
  };

  const handleDeleteSession = async (sessionId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Bu sohbeti silmek istediğinizden emin misiniz? Tüm mesajlar kalıcı olarak silinecektir.')) return;
    try {
      await api.delete(`/admin/chat/sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
      if (selectedSessionId === sessionId) {
        setSelectedSessionId(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Delete chat session error:', err);
    }
  };

  const handleClearMessages = async (sessionId: string) => {
    if (!window.confirm('Bu sohbetin tüm mesajlarını temizlemek istediğinizden emin misiniz?')) return;
    try {
      await api.post('/admin/chat/sessions/clear', { sessionId });
      setMessages([]);
      fetchSessions();
    } catch (err) {
      console.error('Clear chat messages error:', err);
    }
  };

  const handleQuickReplyClick = (text: string) => {
    setReplyText(prev => (prev ? `${prev} ${text}` : text));
  };

  const handleSaveQuickReplies = async () => {
    try {
      const updated = [...editingReplies];
      if (newTitle.trim() && newText.trim()) {
        updated.push({
          id: String(Date.now()),
          title: newTitle.trim(),
          text: newText.trim(),
        });
      }

      await api.post('/admin/chat/quick-replies', { replies: updated });
      setQuickReplies(updated);
      setEditingReplies(updated);
      setNewTitle('');
      setNewText('');
      setShowQuickReplyModal(false);
    } catch (err) {
      console.error('Save quick replies error:', err);
    }
  };

  const handleDeleteQuickReply = (id: string) => {
    setEditingReplies(prev => prev.filter(r => r.id !== id));
  };

  const filteredSessions = sessions.filter(session => {
    if (activeTab === 'needs_human') return session.status === 'needs_human' || session.status === 'human_active';
    if (activeTab === 'ai') return session.status === 'ai';
    return true;
  });

  const selectedSession = sessions.find(s => s.sessionId === selectedSessionId);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 rounded-xl">
            <MessageSquare size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">Canlı Destek & YZ Müşteri İlişkileri</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Web sitesinden gelen canlı müşteri sorularını takip edin ve yanıtlayın.
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowQuickReplyModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-semibold transition-colors"
          >
            <Edit2 size={16} />
            <span>Hızlı Cevapları Düzenle</span>
          </button>

          <button
            onClick={() => { setIsLoading(true); fetchSessions(); }}
            className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl text-xs transition-colors"
            title="Yenile"
          >
            <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[680px]">
        {/* Left Column: Sessions List */}
        <div className="lg:col-span-5 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col overflow-hidden">
          {/* Tabs */}
          <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center space-x-2">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex-1 py-2 text-xs font-semibold rounded-xl transition-all ${
                activeTab === 'all'
                  ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
                  : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
              }`}
            >
              Tümü ({sessions.length})
            </button>
            <button
              onClick={() => setActiveTab('needs_human')}
              className={`flex-1 py-2 text-xs font-semibold rounded-xl transition-all flex items-center justify-center space-x-1 ${
                activeTab === 'needs_human'
                  ? 'bg-amber-500 text-white'
                  : 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400'
              }`}
            >
              <span>Bekleyenler</span>
              <span className="bg-white/20 px-1.5 py-0.2 rounded-full text-[10px]">
                {sessions.filter(s => s.status === 'needs_human' || s.status === 'human_active').length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('ai')}
              className={`flex-1 py-2 text-xs font-semibold rounded-xl transition-all ${
                activeTab === 'ai'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400'
              }`}
            >
              YZ (Sohbet)
            </button>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
            {filteredSessions.length === 0 ? (
              <div className="p-8 text-center text-slate-400 dark:text-slate-500 text-xs">
                Aktif sohbet oturumu bulunmuyor.
              </div>
            ) : (
              filteredSessions.map(session => {
                const isSelected = session.sessionId === selectedSessionId;
                const isNeedsHuman = session.status === 'needs_human';
                const isHumanActive = session.status === 'human_active';

                return (
                  <div
                    key={session.sessionId}
                    onClick={() => setSelectedSessionId(session.sessionId)}
                    className={`p-4 cursor-pointer transition-all hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                      isSelected ? 'bg-rose-50/60 dark:bg-rose-500/10 border-l-4 border-rose-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-sm text-slate-800 dark:text-white">
                          {session.customerName || 'Misafir Müşteri'}
                        </span>
                        {isNeedsHuman && (
                          <span className="animate-pulse bg-amber-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                            Canlı Temsilci!
                          </span>
                        )}
                        {isHumanActive && (
                          <span className="bg-emerald-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                            Temsilci Yanıtladı
                          </span>
                        )}
                        {session.status === 'ai' && (
                          <span className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center space-x-1">
                            <Bot size={10} />
                            <span>YZ Yanıtlıyor</span>
                          </span>
                        )}
                      </div>
                      <span className="text-[10px] text-slate-400">
                        {session.updatedAt ? new Date(session.updatedAt).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>

                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {session.lastMessage || 'Mesaj başlatıldı...'}
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Chat Box */}
        <div className="lg:col-span-7 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200/80 dark:border-slate-800 shadow-sm flex flex-col overflow-hidden">
          {selectedSessionId ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 flex items-center justify-center font-bold text-sm">
                    {selectedSession?.customerName?.charAt(0) || 'M'}
                  </div>
                  <div>
                    <h3 className="font-bold text-sm text-slate-900 dark:text-white">
                      {selectedSession?.customerName || 'Misafir Müşteri'}
                    </h3>
                    <p className="text-[11px] text-slate-400">
                      Oturum ID: {selectedSessionId}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-xl font-medium uppercase tracking-wider text-[10px]">
                    {selectedSession?.status === 'needs_human' ? '⚠️ Canlı Temsilci Bekliyor' : selectedSession?.status === 'human_active' ? '👤 Temsilci Aktif' : selectedSession?.status === 'closed' ? '✅ Kapalı' : '🤖 YZ Aktif'}
                  </span>

                  {selectedSession?.status === 'ai' && (
                    <button
                      onClick={() => handleUpdateStatus('human_active')}
                      className="px-3 py-1.5 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-xl text-xs font-semibold transition-colors flex items-center space-x-1 border border-amber-200/60"
                      title="Sohbeti Temsilciye Al (YZ'yi Durdur)"
                    >
                      <UserCheck size={14} />
                      <span>Temsilciye Al (YZ Durdur)</span>
                    </button>
                  )}

                  {selectedSession?.status !== 'ai' && (
                    <button
                      onClick={() => handleUpdateStatus('ai')}
                      className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 rounded-xl text-xs font-semibold transition-colors flex items-center space-x-1 border border-indigo-200/60"
                      title="YZ'ye Devret (Yapay Zekayı Aktif Et)"
                    >
                      <Bot size={14} />
                      <span>YZ'ye Devret</span>
                    </button>
                  )}

                  {selectedSession?.status !== 'closed' && (
                    <button
                      onClick={() => handleUpdateStatus('closed')}
                      className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 rounded-xl text-xs font-semibold transition-colors flex items-center space-x-1"
                      title="Sohbeti Kapat"
                    >
                      <CheckCircle size={14} />
                      <span>Sohbeti Kapat</span>
                    </button>
                  )}

                  <button
                    onClick={() => handleClearMessages(selectedSessionId)}
                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-semibold transition-colors flex items-center space-x-1"
                    title="Sohbet Mesajlarını Temizle"
                  >
                    <RefreshCw size={14} />
                    <span>Temizle</span>
                  </button>

                  <button
                    onClick={() => handleDeleteSession(selectedSessionId)}
                    className="px-3 py-1.5 bg-rose-100 hover:bg-rose-200 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300 rounded-xl text-xs font-semibold transition-colors flex items-center space-x-1 border border-rose-300/40"
                    title="Sohbeti Kalıcı Olarak Sil"
                  >
                    <Trash2 size={14} />
                    <span>Sohbeti Sil</span>
                  </button>
                </div>
              </div>

              {/* Messages Stream */}
              <div ref={messagesContainerRef} className="flex-1 p-4 overflow-y-auto space-y-3 bg-slate-50/30 dark:bg-slate-950/20 text-xs">
                {messages.map(msg => {
                  const isCustomer = msg.sender === 'customer';
                  const isAdmin = msg.sender === 'admin';

                  return (
                    <div
                      key={msg.id}
                      className={`flex flex-col ${isCustomer ? 'items-start' : 'items-end'}`}
                    >
                      <span className="text-[10px] text-slate-400 font-medium mb-1 px-1">
                        {isCustomer ? 'Müşteri' : isAdmin ? `👤 ${msg.adminName || 'Admin'}` : '🤖 YZ Temsilcisi'}
                      </span>
                      <div
                        className={`max-w-[80%] p-3.5 rounded-2xl leading-relaxed whitespace-pre-wrap ${
                          isCustomer
                            ? 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 rounded-tl-xs shadow-2xs'
                            : isAdmin
                            ? 'bg-rose-600 text-white rounded-tr-xs shadow-md font-medium'
                            : 'bg-indigo-50 text-indigo-950 dark:bg-indigo-950/40 dark:text-indigo-200 border border-indigo-200/60 dark:border-indigo-800/40 rounded-tr-xs'
                        }`}
                      >
                        {msg.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Quick Reply Pills */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800/40 border-t border-slate-100 dark:border-slate-800">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Ön Tanımlı Hızlı Cevaplar (Tıkla ve Gönder)
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto">
                  {quickReplies.map(reply => (
                    <button
                      key={reply.id}
                      onClick={() => handleQuickReplyClick(reply.text)}
                      className="bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-300 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-rose-500 text-slate-700 dark:text-slate-300 text-[11px] font-medium px-2.5 py-1.5 rounded-xl transition-all shadow-2xs"
                    >
                      {reply.title}
                    </button>
                  ))}
                </div>
              </div>

              {/* Input Form */}
              <form onSubmit={handleSendReply} className="p-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex items-center space-x-3">
                <input
                  type="text"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Müşteriye doğrudan yanıt yazın..."
                  className="flex-1 bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 text-xs px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500"
                />
                <button
                  type="submit"
                  disabled={!replyText.trim() || isSending}
                  className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-5 py-3 rounded-xl font-semibold text-xs transition-all shadow-md flex items-center space-x-2"
                >
                  <Send size={14} />
                  <span>Yanıtla</span>
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400">
              <MessageSquare size={48} className="text-slate-300 dark:text-slate-700 mb-3" />
              <p className="font-semibold text-slate-700 dark:text-slate-300 text-sm">Sohbet Seçilmedi</p>
              <p className="text-xs text-slate-400 mt-1 max-w-sm">
                Sol taraftaki listeden bir müşteri sohbetini seçerek mesajlaşmayı görüntüleyebilir ve canlı yanıt verebilirsiniz.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Reply Editor Modal */}
      {showQuickReplyModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 w-full max-w-xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-3">
              <h3 className="font-bold text-base text-slate-900 dark:text-white">
                Ön Tanımlı Hızlı Cevapları Yönet
              </h3>
              <button
                onClick={() => setShowQuickReplyModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white text-lg font-bold"
              >
                ×
              </button>
            </div>

            {/* Form to add new quick reply */}
            <div className="space-y-3 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Yeni Şablon Ekle</p>
              <input
                type="text"
                placeholder="Başlık (Ör: Sipariş Yolda 🚚)"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="w-full bg-white dark:bg-slate-800 text-xs px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700"
              />
              <textarea
                placeholder="Şablon Metni (Ör: Siparişiniz kuryemizce teslim edilmek üzere yola çıkmıştır.)"
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                rows={2}
                className="w-full bg-white dark:bg-slate-800 text-xs px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700"
              />
            </div>

            {/* List of existing quick replies */}
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {editingReplies.map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/40 rounded-xl border border-slate-100 dark:border-slate-800">
                  <div>
                    <p className="font-bold text-xs text-slate-800 dark:text-slate-200">{r.title}</p>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">{r.text}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteQuickReply(r.id)}
                    className="p-1 text-rose-500 hover:bg-rose-50 rounded-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex justify-end space-x-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <button
                onClick={() => setShowQuickReplyModal(false)}
                className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold"
              >
                İptal
              </button>
              <button
                onClick={handleSaveQuickReplies}
                className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-semibold"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveChatManager;
